import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import LocationPrompt from './components/LocationPrompt';
import { usePageTracker } from './hooks/usePageTracker';
import { NoIndex } from './components/SEO';
import './App.css';

// Lazy-load all pages for loading indicator on route transitions
const LandingPage = React.lazy(() => import('./pages/LandingPage'));
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const AuthCallback = React.lazy(() => import('./pages/AuthCallback'));
const UserTypeSelection = React.lazy(() => import('./pages/UserTypeSelection'));
const BuyerDashboard = React.lazy(() => import('./pages/BuyerDashboard'));
const SupplierDashboard = React.lazy(() => import('./pages/SupplierDashboard'));
const ItemDetailPage = React.lazy(() => import('./pages/ItemDetailPage'));
const ItemsPage = React.lazy(() => import('./pages/ItemsPage'));
const QuotationsPage = React.lazy(() => import('./pages/QuotationsPage'));
const ComparePage = React.lazy(() => import('./pages/ComparePage'));
const WishlistPage = React.lazy(() => import('./pages/WishlistPage'));
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));
const AboutPage = React.lazy(() => import('./pages/AboutPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const CityAdminDashboard = React.lazy(() => import('./pages/CityAdminDashboard'));
const ServicesPage = React.lazy(() => import('./pages/ServicesPage'));
const ServiceProviderDashboard = React.lazy(() => import('./pages/ServiceProviderDashboard'));
const ConstructionCostCalculator = React.lazy(() => import('./pages/ConstructionCostCalculator'));
const ToolsPage = React.lazy(() => import('./pages/ToolsPage'));
const CreateRFQ = React.lazy(() => import('./pages/CreateRFQ'));
const RFQDashboard = React.lazy(() => import('./pages/RFQDashboard'));
const BuyerRegistration = React.lazy(() => import('./pages/BuyerRegistration'));
const SupplierSEOPage = React.lazy(() => import('./pages/SupplierSEOPage'));
const SupplierVerification = React.lazy(() => import('./pages/SupplierVerification'));
const AdminLoginPage = React.lazy(() => import('./pages/AdminLoginPage'));

const NirmanLoader = () => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-background">
    <div className="relative mb-4">
      <div className="h-16 w-16 rounded-xl border-4 border-orange-200 animate-pulse overflow-hidden">
        <img src="/assets/nirman_dwar_logo.png" alt="NirmanDwar" className="h-full w-full object-cover" />
      </div>
      <div className="absolute -bottom-1 -right-1 h-5 w-5 bg-orange-500 rounded-full animate-bounce" />
    </div>
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-1.5 rounded-full bg-orange-500 animate-bounce" style={{ animationDelay: '0ms' }} />
      <div className="h-1.5 w-1.5 rounded-full bg-orange-400 animate-bounce" style={{ animationDelay: '150ms' }} />
      <div className="h-1.5 w-1.5 rounded-full bg-orange-300 animate-bounce" style={{ animationDelay: '300ms' }} />
    </div>
    <p className="text-xs text-muted-foreground mt-2 font-medium tracking-wide">Loading NirmanDwar...</p>
  </div>
);

const ProtectedRoute = ({ children, allowedTypes }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <NirmanLoader />;
  if (!user) return <Navigate to="/login" state={{ redirectTo: location.pathname }} replace />;
  if (allowedTypes && !allowedTypes.includes(user.user_type) && user.user_type !== 'pending') return <Navigate to="/" />;
  return children;
};

import { LanguageProvider } from './context/LanguageContext';

const PageTracker = () => {
  const { user, selectedCity } = useAuth();
  usePageTracker(user, selectedCity);
  return null;
};

function App() {
  return (
    <HelmetProvider>
    <LanguageProvider>
    <AuthProvider>
      <Router>
        <PageTracker />
        <Toaster position="top-right" />
        <LocationPrompt />
        <Suspense fallback={<NirmanLoader />}>
        <Routes>
          {/* Public - Indexable */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/register/buyer" element={<BuyerRegistration />} />
          <Route path="/register/supplier" element={<UserTypeSelection directSupplier={true} />} />
          <Route path="/items" element={<ItemsPage />} />
          <Route path="/items/:itemId" element={<ItemDetailPage />} />
          <Route path="/compare" element={<ComparePage />} />
          <Route path="/tools/construction-cost-calculator" element={<ConstructionCostCalculator />} />
          <Route path="/tools" element={<ToolsPage />} />
          <Route path="/service-providers" element={<ProtectedRoute allowedTypes={['buyer', 'supplier', 'admin', 'city_admin', 'service_provider', 'pending']}><ServicesPage /></ProtectedRoute>} />
          <Route path="/suppliers/:city/:category" element={<SupplierSEOPage />} />

          {/* Restricted - NoIndex */}
          <Route path="/nd-admin-access" element={<NoIndex><AdminLoginPage /></NoIndex>} />
          <Route path="/verify/supplier/:token" element={<NoIndex><SupplierVerification /></NoIndex>} />
          <Route path="/auth/callback" element={<NoIndex><AuthCallback /></NoIndex>} />
          <Route path="/select-type" element={<NoIndex><UserTypeSelection /></NoIndex>} />
          <Route path="/buyer-dashboard" element={<NoIndex><BuyerDashboard /></NoIndex>} />
          <Route path="/supplier-dashboard" element={<ProtectedRoute allowedTypes={['supplier']}><NoIndex><SupplierDashboard /></NoIndex></ProtectedRoute>} />
          <Route path="/quotations" element={<ProtectedRoute allowedTypes={['buyer', 'supplier']}><NoIndex><QuotationsPage /></NoIndex></ProtectedRoute>} />
          <Route path="/wishlist" element={<ProtectedRoute allowedTypes={['buyer']}><NoIndex><WishlistPage /></NoIndex></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute allowedTypes={['admin']}><NoIndex><AdminDashboard /></NoIndex></ProtectedRoute>} />
          <Route path="/city-admin" element={<ProtectedRoute allowedTypes={['city_admin']}><NoIndex><CityAdminDashboard /></NoIndex></ProtectedRoute>} />
          <Route path="/service-provider-dashboard" element={<ProtectedRoute allowedTypes={['service_provider']}><NoIndex><ServiceProviderDashboard /></NoIndex></ProtectedRoute>} />
          <Route path="/rfq/create" element={<ProtectedRoute allowedTypes={['buyer', 'pending']}><NoIndex><CreateRFQ /></NoIndex></ProtectedRoute>} />
          <Route path="/rfq" element={<ProtectedRoute allowedTypes={['buyer', 'supplier', 'pending']}><NoIndex><RFQDashboard /></NoIndex></ProtectedRoute>} />
        </Routes>
        </Suspense>
      </Router>
    </AuthProvider>
    </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
